import { Edit, Lock, NavigateNext } from '@mui/icons-material';
import {
  Breadcrumbs,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { generatePath, NavLink, useParams } from 'react-router-dom';
import { paths } from '../../../../../../routing/Router/paths';
import { auth } from '../../../../../../utils/auth';
import { CategoriesContext } from '../../../../categories.context';
import { DEFAULT_ROOT_CATEGORY } from '../../../../constants';
import { DataTestIdListPage } from '../../../../tests/constants';
import type { Category } from '../../../../types';
import { navigateTo } from '../../../../utils';
import { MAX_SEGMENTS_BREADCRUMBS } from './constants';
import { useStyles } from './styles';

interface BreadCrumb {
  id: string;
  isActive?: boolean;
  pathname?: string;
  title: string | JSX.Element;
}

const formatTitleMap: Record<string, string> = {
  event: 'Évènements',
  channel: 'Channel',
  guide: 'Guide pratique',
  category: 'Catégories',
};

export const BreadCrumbs: FC = () => {
  const { activeCategory, categories, isCategoriesLoading } = useContext(
    CategoriesContext,
  );
  const routeParams = useParams();
  const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumb[]>([]);
  const classes = useStyles({
    isRootCategoryActive: false,
  });
  const theme = useTheme();

  const isAllowedToInteractWithRootCategory = auth.user?.isSuperAdmin();

  useEffect(() => {
    const { brandKey, format, lvl1, lvl2, lvl3, lvl4, lvl5 } = routeParams;
    const newBreadCrumbs: BreadCrumb[] = [];

    const EditButton = ({ categoryId }: { categoryId: string }) => (
      <Tooltip title="Modifier le template">
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            const search = new URLSearchParams({
              from: 'Breadcrumbs',
              categoryId,
            }).toString();

            const pathname = `${generatePath(paths.PAGE_EDIT, {
              brandKey: routeParams.brandKey,
              format: routeParams.format,
            })}/${categoryId}`;

            navigateTo({
              pathname,
              opts: { search },
            });
          }}
        >
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    );

    // Find level 2 category and current active category
    const level2Category = categories.find(
      (cat) =>
        cat.level === 2 &&
        cat.format.toLowerCase() === format.toLowerCase() &&
        cat.brandKey === brandKey,
    );

    // Find current active category and build path
    const currentLevelId = lvl5 || lvl4 || lvl3 || lvl2 || lvl1;
    const currentCategory = currentLevelId
      ? categories.find((cat) => cat.id === currentLevelId)
      : level2Category; // Default to level 2 if no specific level is selected

    // Only show root level breadcrumb if we're not viewing a level 1 category
    if (
      isAllowedToInteractWithRootCategory &&
      (!currentCategory || currentCategory.level > 1)
    ) {
      // For admin: show level 1 category from DEFAULT_ROOT_CATEGORY
      const level1Category = categories.find(
        (cat) =>
          cat.level === 1 &&
          cat.format.toLowerCase() === format.toLowerCase() &&
          cat.brandKey === DEFAULT_ROOT_CATEGORY.brandKey,
      );

      if (level1Category) {
        // eslint-disable-next-line fp/no-mutating-methods
        newBreadCrumbs.push({
          id: level1Category.id,
          pathname: undefined,
          title: (
            <Stack direction="row" alignItems="center" spacing={1}>
              <span className={classes.wrapperBreadCrumbRootCategory}>
                <Chip color="primary" label="Racine" size="small" />
                {level1Category.title}
              </span>
              {!isCategoriesLoading &&
                activeCategory?.id === level1Category.id && (
                  <EditButton categoryId={level1Category.id} />
                )}
            </Stack>
          ),
        });
      }
    } else if (!isAllowedToInteractWithRootCategory) {
      // For non-admin: show format title
      // eslint-disable-next-line fp/no-mutating-methods
      newBreadCrumbs.push({
        id: 'root',
        pathname: undefined,
        title: (
          <span className={classes.wrapperBreadCrumbRootCategory}>
            <Chip
              color="primary"
              label="Racine"
              size="small"
              deleteIcon={<Lock />}
              onDelete={() => {}}
            />
            {formatTitleMap[format.toLowerCase()] || format}
          </span>
        ),
      });
    }

    if (currentCategory) {
      // Build category path from root to current
      const categoryPath: Category[] = [currentCategory];

      // Recursive function to build the path
      const buildCategoryPath = (
        path: Category[],
        parentId: string | undefined,
      ): Category[] => {
        if (parentId === undefined) {
          return path;
        }

        const parent = categories.find((category) => category.id === parentId);
        if (!parent) {
          return path;
        }

        // Skip adding level 1 categories completely
        if (parent.level === 1) {
          return path;
        }

        // eslint-disable-next-line fp/no-mutating-methods
        path.unshift(parent);

        return buildCategoryPath(path, parent.parent?.id);
      };

      // Build the path starting from the current category's parent
      buildCategoryPath(categoryPath, currentCategory.parent?.id);

      // Add all categories in path to breadcrumbs
      categoryPath.forEach((category) => {
        const isCurrentCategory = category.id === currentCategory.id;
        const path = categoryPath
          .slice(0, categoryPath.indexOf(category) + 1)
          .map((cat) => `/${cat.id}`)
          .join('');

        // Show edit button for current category if:
        // - Not loading
        // - Is current category
        // - Either level > 1 OR (level 1 AND super admin)
        const showEditButton =
          !isCategoriesLoading &&
          isCurrentCategory &&
          (category.level > 1 ||
            (category.level === 1 && isAllowedToInteractWithRootCategory));

        // Determine if this breadcrumb should be clickable
        // - Level 1 categories are never clickable
        // - All other categories (level 2 and above) are clickable
        const isClickable = category.level > 1;

        // eslint-disable-next-line fp/no-mutating-methods
        newBreadCrumbs.push({
          id: category.id,
          isActive: isCurrentCategory,
          // Only make clickable if it's not level 1
          pathname: isClickable
            ? `${generatePath(paths.PAGE_LIST, {
                brandKey,
                format,
              })}${path}`
            : undefined,
          title: (
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>{category.title}</span>
              {showEditButton && <EditButton categoryId={category.id} />}
            </Stack>
          ),
        });
      });
    } else if (level2Category && !currentLevelId) {
      // Add default level 2 category if no specific category is selected
      // eslint-disable-next-line fp/no-mutating-methods
      newBreadCrumbs.push({
        id: level2Category.id,
        isActive: true,
        pathname: generatePath(paths.PAGE_LIST, { brandKey, format }),
        title: (
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{level2Category.title}</span>
            {!isCategoriesLoading && (
              <EditButton categoryId={level2Category.id} />
            )}
          </Stack>
        ),
      });
    }

    setBreadCrumbs(newBreadCrumbs);
  }, [
    categories,
    routeParams,
    activeCategory,
    isAllowedToInteractWithRootCategory,
    classes.wrapperBreadCrumbRootCategory,
    isCategoriesLoading,
  ]);

  return (
    <div className={classes.wrapper}>
      <Breadcrumbs
        aria-label="breadcrumb"
        maxItems={MAX_SEGMENTS_BREADCRUMBS}
        separator={<NavigateNext fontSize="small" />}
      >
        {breadCrumbs.map((breadCrumb: BreadCrumb, index: number) => {
          const shouldShowNavLink = breadCrumb.pathname && !breadCrumb.isActive;

          if (shouldShowNavLink && breadCrumb.pathname) {
            return (
              <NavLink
                className={classes.breadCrumbLink}
                data-testid={DataTestIdListPage.LIST_BREADCRUMBS_LINK}
                key={breadCrumb.id || index}
                to={breadCrumb.pathname}
                style={{
                  color: theme.palette.grey[500],
                }}
              >
                {breadCrumb.title}
              </NavLink>
            );
          }

          return (
            <Typography
              className={classes.breadCrumbLink}
              data-testid={DataTestIdListPage.LIST_BREADCRUMBS_TEXT}
              key={breadCrumb.id || index}
              sx={{
                color: breadCrumb.isActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[500],
              }}
            >
              {breadCrumb.title}
            </Typography>
          );
        })}

        {isCategoriesLoading && (
          <CircularProgress
            className={classes.loader}
            color="primary"
            size={20}
          />
        )}
      </Breadcrumbs>
    </div>
  );
};
